import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Heart, ChevronLeft, ChevronRight, Music } from 'lucide-react'

/**
 * Les dédicaces, UNE à la fois, en grand.
 *
 * Remplace l'ancien bandeau qui faisait défiler les 20 messages collés
 * bout à bout en petits caractères (illisible sur téléphone, et très
 * lourd pour les box TV : un calque de plusieurs dizaines de milliers
 * de pixels, d'où les saccades).
 *
 * variant="card" : téléphone — carte, texte lisible, glisser pour changer.
 * variant="tv"   : bandeau TV — une ligne, gros texte ; si le message est
 *                  trop long, il défile doucement une fois, puis le suivant.
 *
 * La durée d'affichage dépend de la longueur du message. Le passage à la
 * dédicace suivante est piloté par une minuterie JavaScript (et non par la
 * fin d'une animation CSS) : certaines règles CSS du projet accélèrent
 * toutes les animations sur téléphone, ce qui faisait défiler le carrousel
 * à toute vitesse. La barre de progression n'est plus que décorative.
 */

const clamp = (v, min, max) => Math.max(min, Math.min(max, v))

/**
 * Minuterie qu'on peut mettre en pause : à la reprise, elle repart
 * du temps qui restait (et non de zéro).
 */
function useRotationTimer({ itemKey, duration, paused, enabled, onDone }) {
  const remainingRef = useRef(duration)
  const doneRef = useRef(onDone)
  doneRef.current = onDone

  // Nouvelle dédicace : on repart de sa durée complète
  useEffect(() => { remainingRef.current = duration }, [itemKey, duration])

  useEffect(() => {
    if (!enabled || paused) return undefined
    const startedAt = Date.now()
    const t = setTimeout(() => doneRef.current(), remainingRef.current)
    return () => {
      clearTimeout(t)
      remainingRef.current = Math.max(800, remainingRef.current - (Date.now() - startedAt))
    }
  }, [itemKey, paused, enabled])
}

// Temps de lecture : ~4 s + 55 ms par caractère, entre 6 et 16 s
const readingTime = (text) => clamp(4000 + text.length * 55, 6000, 16000)

function normalise(list) {
  return (list || [])
    .map((d) => ({
      id: d.id,
      message: String(d.message || '').trim(),
      to: String(d.to || '').trim(),
      from: String(d.from || '').trim(),
      song: String(d.song || '').trim()
    }))
    .filter((d) => d.message)
}

export default function DedicaceSpotlight({ dedicaces, variant = 'card', onSeeAll }) {
  const items = useMemo(() => normalise(dedicaces), [dedicaces])
  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)
  const touchRef = useRef(null)

  // Garder un index valide quand la liste change (nouvelle dédicace…)
  useEffect(() => {
    if (index >= items.length) setIndex(0)
  }, [items.length, index])

  const count = items.length
  const current = count ? items[Math.min(index, count - 1)] : null
  const go = (step) => count && setIndex((i) => (i + step + count) % count)
  const next = () => go(1)
  const itemKey = current ? `${current.id || ''}-${index}` : 'none'

  // Téléphone : la minuterie tourne ici. TV : dans TvLine (durée
  // allongée si le texte doit défiler).
  useRotationTimer({
    itemKey,
    duration: current ? readingTime(current.message) : 0,
    paused,
    enabled: variant !== 'tv' && count > 1,
    onDone: next
  })

  if (variant === 'tv') {
    return <TvLine current={current} index={index} count={count} onDone={next} />
  }

  const onTouchStart = (e) => {
    touchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }
    setPaused(true)
  }

  const onTouchEnd = (e) => {
    const start = touchRef.current
    touchRef.current = null
    setPaused(false)
    if (!start) return
    const dx = e.changedTouches[0].clientX - start.x
    const dy = e.changedTouches[0].clientY - start.y
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) go(dx < 0 ? 1 : -1)
  }

  const duration = current ? readingTime(current.message) : 0

  return (
    <section
      className={`j-spot ${paused ? 'is-paused' : ''}`}
      aria-roledescription="carrousel"
      aria-label="Dédicaces des auditeurs"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="j-spot__head">
        <span className="j-spot__label">
          <Heart size={20} fill="currentColor" strokeWidth={0} />
          Dédicaces
          {count > 1 && <span className="j-spot__count">{index + 1} sur {count}</span>}
        </span>

        {count > 1 && (
          <div className="j-spot__nav">
            <button type="button" onClick={() => go(-1)} aria-label="Dédicace précédente">
              <ChevronLeft size={20} />
            </button>
            <button type="button" onClick={() => go(1)} aria-label="Dédicace suivante">
              <ChevronRight size={20} />
            </button>
          </div>
        )}
      </div>

      {current ? (
        <div className="j-spot__slide" key={current.id || index} aria-live="polite">
          {current.to && (
            <div className="j-spot__to">Pour <strong>{current.to}</strong></div>
          )}
          <p className="j-spot__msg">« {current.message} »</p>
          <div className="j-spot__meta">
            {current.from && <span className="from">{current.from}</span>}
            {current.song && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <Music size={14} /> {current.song}
              </span>
            )}
          </div>
        </div>
      ) : (
        <div className="j-spot__slide">
          <p className="j-spot__msg" style={{ fontSize: '1.1rem', color: 'var(--j-muted)' }}>
            Aucune dédicace pour l'instant. Soyez le premier à en envoyer une.
          </p>
        </div>
      )}

      {onSeeAll && (
        <button type="button" className="j-spot__cta" onClick={onSeeAll}>
          {count ? 'Toutes les dédicaces' : 'Envoyer une dédicace'}
          <ChevronRight size={16} />
        </button>
      )}

      {count > 1 && (
        <div className="j-spot__bar" aria-hidden="true">
          <i
            className="j-timer"
            key={`${itemKey}-bar`}
            style={{ animationDuration: `${duration}ms` }}
          />
        </div>
      )}
    </section>
  )
}

// ---------------------------------------------------------------------
// Variante TV
// ---------------------------------------------------------------------
function TvLine({ current, index, count, onDone }) {
  const viewportRef = useRef(null)
  const textRef = useRef(null)
  const [shift, setShift] = useState(0)

  const text = current
    ? `${current.message}${current.from ? ` — ${current.from}` : ''}${current.song ? ` (${current.song})` : ''}`
    : ''

  // Une seule mesure par message (pas à chaque image)
  useLayoutEffect(() => {
    const vp = viewportRef.current
    const el = textRef.current
    if (!vp || !el) return
    const overflow = el.scrollWidth - vp.clientWidth
    setShift(overflow > 8 ? -overflow : 0)
  }, [text])

  const base = readingTime(text)
  // Défilement doux (~55 px/s) + pauses de lecture au début et à la fin
  const duration = shift ? Math.max(base, (Math.abs(shift) / 55) * 1000 / 0.7) : base

  useRotationTimer({
    itemKey: `${current?.id || ''}-${index}`,
    duration,
    paused: false,
    enabled: count > 1,
    onDone
  })

  return (
    <div className="j-spot-tv">
      <div className="j-spot-tv__viewport" ref={viewportRef}>
        {current ? (
          <div className="j-spot-tv__item" key={current.id || index}>
            <span
              ref={textRef}
              className={`j-spot-tv__text ${shift ? 'is-scrolling' : ''}`}
              style={shift ? { '--j-shift': `${shift}px`, animationDuration: `${duration}ms` } : undefined}
            >
              {current.to && <span className="j-spot-tv__to">Pour {current.to} : </span>}
              {current.message}
              {current.from && <span className="j-spot-tv__from"> — {current.from}</span>}
              {current.song && <span className="j-spot-tv__from"> ({current.song})</span>}
            </span>
          </div>
        ) : (
          <span className="j-spot-tv__text" ref={textRef}>
            Aucune dédicace pour le moment
          </span>
        )}
      </div>
      {count > 1 && (
        <span className="j-spot-tv__dots">{index + 1}/{count}</span>
      )}
    </div>
  )
}
